import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { BUSINESS, SITE } from '../data/seoContent';

const STEPS = [
  { h: 'Seating and size', p: 'Count who will use it most nights, not just for parties. Sit in the seats — loungers, captain’s chairs and seat depth feel very different. Measure your lanai, patio or pad, plus the path for delivery (gates, doors, pool cages).' },
  { h: 'Jets and pumps', p: 'More jets is not automatically better. Look at where the jets hit (neck, shoulders, back, calves, feet), how many pumps drive them, and whether you can adjust pressure. A wet test at our Naples showroom is the fastest way to tell.' },
  { h: 'Energy efficiency', p: 'In Southwest Florida you will use a hot tub year-round. Full-foam insulation, a tight-fitting quality cover and an efficient circulation pump keep monthly costs down. Ask us for a running-cost estimate on the exact model you like.' },
  { h: 'Water care', p: 'About 85% of hot tub service calls and repair bills trace back to water chemistry. Look at the filtration, ozone or UV options — and decide whether you want to do it yourself or have Spa Butler handle it with chemicals included.' },
  { h: 'Standard, serviceable parts', p: 'Tubs built on standard Balboa/Waterway components are easier and cheaper to repair for the life of the spa. Proprietary parts can mean long waits and high bills.' },
  { h: 'Warranty', p: 'Read what is covered and for how long: shell structure, surface, plumbing, equipment and labor. Then ask who actually does the warranty work locally.' },
  { h: 'Installation and power', p: 'Most acrylic hot tubs need a 240V hard-wired circuit; plug-and-play tubs and some saunas run on a standard 110V/120V outlet. You also need a level, solid base. We check power, base and access before delivery.' },
  { h: 'Service after the sale', p: 'Buy from someone who will still be there. Leisure Wellness Naples delivers, installs and services everything we sell with our own local team — no 1-800 numbers.' },
];

const guideLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Hot Tub Buying Guide for Naples & Southwest Florida',
  description: 'What to look for when buying a hot tub in Naples, FL: seating, jets and pumps, energy efficiency, water care, serviceable parts, warranty, power and service.',
  author: { '@type': 'Organization', name: 'Leisure Wellness Naples' },
  publisher: { '@id': `${SITE}/#business` },
  image: `${SITE}/share/hot-tub-buying-guide.jpg`,
  mainEntityOfPage: `${SITE}/hot-tub-buying-guide`,
  datePublished: '2026-10-27',
};

const BuyingGuidePage = () => (
  <>
    <Seo page="/hot-tub-buying-guide" jsonLd={guideLd} />
    <article className="pt-36 pb-20" data-testid="buying-guide-page">
      <div className="max-w-3xl mx-auto px-4">
        <p className="text-sm font-semibold uppercase tracking-wider text-[#F88200] mb-2">Buying Guide</p>
        <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-[#0E3D95] mb-5">Hot Tub Buying Guide for Naples & Southwest Florida</h1>
        <p className="text-lg text-slate-700 mb-8">
          Shopping for a hot tub in Naples, Marco Island, Bonita Springs, Estero or Fort Myers? Here is what our wellness experts tell every customer to check — in order. Then come try them at our showroom, 6000 Taylor Rd, Suite 8, Naples, FL 34109.
        </p>
        {STEPS.map((s, i) => (
          <section key={s.h} className="mb-7" data-testid={`guide-step-${i}`}>
            <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold uppercase text-[#0E3D95] mb-2">{i + 1}. {s.h}</h2>
            <p className="text-slate-700 leading-relaxed">{s.p}</p>
          </section>
        ))}
        <div className="mt-10 p-6 rounded-xl bg-[#0E3D95] text-white">
          <p className="font-bold text-xl mb-2">Try before you buy</p>
          <p className="text-white/80 mb-4">Compare PDC Spas and ClearWater Spas hot tubs side by side — from value-priced ClearWater to the luxury PDC Midnight Series.</p>
          <div className="flex flex-wrap gap-3">
            <a href={`tel:${BUSINESS.tel}`} className="btn-primary px-6 py-3" data-testid="guide-call-btn">Call {BUSINESS.phone}</a>
            <Link to="/hot-tubs" className="px-6 py-3 rounded-full bg-white text-[#0E3D95] font-bold" data-testid="guide-shop-btn">Shop hot tubs</Link>
          </div>
        </div>
      </div>
    </article>
  </>
);

export default BuyingGuidePage;
